/**
 * KnowSkills Component
 *
 * Isolated — does NOT touch or modify any existing component.
 * Rendered as a tab inside BrainOps.js only.
 *
 * Full port of skill-extractor UI:
 *   - Team member list with search
 *   - Person info card with AutomationRing
 *   - 3 sub-tabs: Skill Clusters, Activity Timeline, Cowork Handoff
 *   - Download SKILL.md / JSON
 *   - Expandable evidence snippets
 *   - Recharts bar chart for activity timeline
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users,
  Zap,
  Clock,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  X,
  Cpu,
  Layers,
  ArrowLeft,
  Download,
  Search,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { SERVER_URL } from '../../utils/serverConfig';
import { resolveUserAvatar } from '../../utils/userAvatar';
import toast from 'react-hot-toast';

// ---------------------------------------------------------------------------
// Color helpers (matching skill-extractor exactly)
// ---------------------------------------------------------------------------
const automationColor = (score) => {
  if (score >= 0.7) return '#059669'; // green
  if (score >= 0.4) return '#D97706'; // amber
  return '#EF4444'; // red
};

const automationLabel = (score) => {
  if (score >= 0.7) return 'Highly Automatable';
  if (score >= 0.4) return 'Partially Automatable';
  return 'Manual Review Needed';
};

const automationBg = (score, isDark) => {
  if (isDark) return score >= 0.7 ? '#052e1620' : score >= 0.4 ? '#451a0320' : '#450a0a20';
  if (score >= 0.7) return '#ecfdf5';
  if (score >= 0.4) return '#fffbeb';
  return '#fef2f2';
};

// Intensity color interpolation (slate-300 → indigo-500)
const intensityColor = (intensity) => {
  const low = { r: 203, g: 213, b: 225 };
  const high = { r: 99, g: 102, b: 241 };
  const r = Math.round(low.r + (high.r - low.r) * intensity);
  const g = Math.round(low.g + (high.g - low.g) * intensity);
  const b = Math.round(low.b + (high.b - low.b) * intensity);
  return `rgb(${r},${g},${b})`;
};

const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').slice(0, 2).map((n) => n[0]).join('').toUpperCase();
};

const avatarColors = [
  '#6366f1', '#8b5cf6', '#059669', '#d97706',
  '#f43f5e', '#0ea5e9', '#14b8a6', '#f97316',
];
const getAvatarColor = (name) => {
  if (!name) return avatarColors[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return avatarColors[Math.abs(hash) % avatarColors.length];
};

const downloadFile = (content, filename, type = 'text/plain') => {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const LOADING_STEPS = [
  'Connecting to database...',
  'Fetching screenshots & sessions...',
  'Searching activity patterns in Qdrant...',
  'Analysing with TeamGrid AI...',
  'Building skill profile...',
];

// ---------------------------------------------------------------------------
// AutomationRing (SVG circular progress — exact port)
// ---------------------------------------------------------------------------
const AutomationRing = ({ score, size = 120 }) => {
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setAnimated(true), 100);
    return () => clearTimeout(timer);
  }, [score]);

  const radius = (size - 16) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = animated
    ? circumference - score * circumference
    : circumference;
  const color = automationColor(score);
  const percentage = Math.round(score * 100);

  return (
    <div className="flex flex-col items-center gap-1">
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#E2E8F0"
          strokeWidth={8}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={8}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          style={{ transition: 'stroke-dashoffset 1s ease-in-out' }}
        />
        <text
          x={size / 2}
          y={size / 2}
          textAnchor="middle"
          dominantBaseline="central"
          style={{
            transform: 'rotate(90deg)',
            transformOrigin: `${size / 2}px ${size / 2}px`,
            fontSize: size > 100 ? '22px' : '16px',
            fontWeight: 700,
            fill: color,
          }}
        >
          {percentage}%
        </text>
      </svg>
      <span className="text-xs font-medium text-slate-500 text-center">
        Automation Ready
      </span>
    </div>
  );
};

// ---------------------------------------------------------------------------
// SkillClusterCard (with expandable evidence — exact port)
// ---------------------------------------------------------------------------
const SkillClusterCard = ({ cluster, isDark }) => {
  const [showEvidence, setShowEvidence] = useState(false);

  return (
    <div
      className="rounded-xl border shadow-sm p-5 flex flex-col gap-3"
      style={{
        background: isDark ? '#18181b' : '#ffffff',
        borderColor: isDark ? '#27272a' : '#e2e8f0',
        borderLeft: '4px solid #6366f1',
      }}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <h3 className={`font-semibold text-base leading-tight ${isDark ? 'text-white' : 'text-[#0F172A]'}`}>
          {cluster.name}
        </h3>
        {cluster.automatable ? (
          <span className="bg-green-100 text-green-700 text-xs font-medium rounded-full px-2.5 py-0.5 whitespace-nowrap flex-shrink-0">
            ✓ Automatable
          </span>
        ) : (
          <span className="bg-amber-100 text-amber-700 text-xs font-medium rounded-full px-2.5 py-0.5 whitespace-nowrap flex-shrink-0">
            ⚠ Needs Review
          </span>
        )}
      </div>

      {/* Description */}
      <p className={`text-sm leading-relaxed ${isDark ? 'text-zinc-400' : 'text-slate-600'}`}>
        {cluster.description}
      </p>

      {/* Confidence bar */}
      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between">
          <span className={`text-xs ${isDark ? 'text-zinc-500' : 'text-slate-500'}`}>Confidence</span>
          <span className={`text-xs font-medium ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>
            {Math.round(cluster.confidence * 100)}%
          </span>
        </div>
        <div className={`h-1.5 rounded-full overflow-hidden ${isDark ? 'bg-zinc-800' : 'bg-slate-100'}`}>
          <div
            className="h-full bg-indigo-500 rounded-full transition-all duration-700"
            style={{ width: `${Math.round(cluster.confidence * 100)}%` }}
          />
        </div>
      </div>

      {/* Time spent */}
      <p className={`text-xs ${isDark ? 'text-zinc-500' : 'text-slate-500'}`}>
        ~{cluster.time_spent_pct}% of workday
      </p>

      {/* Tools */}
      <div className="flex flex-wrap gap-1.5">
        {(cluster.tools || []).map((tool) => (
          <span
            key={tool}
            className="text-xs rounded-full px-2.5 py-0.5"
            style={{
              background: isDark ? '#27272a' : '#f1f5f9',
              color: isDark ? '#d4d4d8' : '#475569',
            }}
          >
            {tool}
          </span>
        ))}
      </div>

      {/* Workflow pattern */}
      {cluster.workflow_pattern && (
        <div
          className="text-xs rounded-lg p-3 mt-1 leading-relaxed"
          style={{
            fontFamily: 'monospace',
            background: isDark ? '#09090b' : '#f8fafc',
            color: isDark ? '#a1a1aa' : '#475569',
          }}
        >
          {cluster.workflow_pattern}
        </div>
      )}

      {/* Evidence toggle */}
      {cluster.evidence_snippets?.length > 0 && (
        <>
          <button
            onClick={() => setShowEvidence(!showEvidence)}
            className="text-xs font-medium text-left transition-colors duration-150 flex items-center gap-1"
            style={{ color: '#6366f1' }}
          >
            {showEvidence ? 'Hide Evidence' : 'Show Evidence'}
            {showEvidence ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          </button>

          <div
            className="overflow-hidden transition-all duration-300"
            style={{ maxHeight: showEvidence ? '400px' : '0px' }}
          >
            <div className="flex flex-col gap-2 pt-1">
              {cluster.evidence_snippets.map((snippet, i) => (
                <p
                  key={i}
                  className="text-xs rounded p-2 leading-relaxed"
                  style={{
                    fontFamily: 'monospace',
                    background: isDark ? '#09090b' : '#f8fafc',
                    color: isDark ? '#71717a' : '#64748b',
                  }}
                >
                  &ldquo;{snippet}&rdquo;
                </p>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// ActivityTimeline (Recharts bar chart + hour breakdown — exact port)
// ---------------------------------------------------------------------------
const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const block = payload[0].payload;
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-sm max-w-[200px]">
      <p className="font-semibold text-[#0F172A]">{block.hour}</p>
      <p className="text-slate-600 text-xs mt-0.5">{block.activity}</p>
      <p className="text-indigo-600 text-xs mt-0.5 font-medium">{block.app}</p>
      <p className="text-slate-400 text-xs mt-0.5">
        Intensity: {Math.round(block.intensity * 100)}%
      </p>
    </div>
  );
};

const ActivityTimeline = ({ timeline, peakHours, isDark }) => {
  const peakSet = new Set(peakHours || []);

  return (
    <div className="flex flex-col gap-6">
      {/* Bar chart */}
      <div
        className="rounded-xl border p-5"
        style={{
          background: isDark ? '#18181b' : '#ffffff',
          borderColor: isDark ? '#27272a' : '#e2e8f0',
        }}
      >
        <h3 className={`font-semibold mb-4 ${isDark ? 'text-white' : 'text-[#0F172A]'}`}>
          Activity Intensity by Hour
        </h3>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={timeline} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
            <XAxis
              dataKey="hour"
              tick={{ fontSize: 11, fill: isDark ? '#71717a' : '#94A3B8' }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              domain={[0, 1]}
              tick={{ fontSize: 11, fill: isDark ? '#71717a' : '#94A3B8' }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v) => `${Math.round(v * 100)}%`}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="intensity" radius={[4, 4, 0, 0]}>
              {(timeline || []).map((entry, index) => (
                <Cell key={index} fill={intensityColor(entry.intensity)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Hour-by-hour breakdown */}
      <div
        className="rounded-xl border p-5"
        style={{
          background: isDark ? '#18181b' : '#ffffff',
          borderColor: isDark ? '#27272a' : '#e2e8f0',
        }}
      >
        <h3 className={`font-semibold mb-4 ${isDark ? 'text-white' : 'text-[#0F172A]'}`}>
          Hour-by-Hour Breakdown
        </h3>
        <div className="flex flex-col gap-1 max-h-96 overflow-y-auto pr-1">
          {(timeline || []).map((block, i) => {
            const isPeak = peakSet.has(block.hour);
            return (
              <div
                key={i}
                className="flex items-center gap-3 px-3 py-2 rounded-lg transition-colors duration-100"
                style={{
                  background: isPeak
                    ? (isDark ? '#1e1b4b20' : '#eef2ff')
                    : undefined,
                }}
              >
                <div
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: intensityColor(block.intensity) }}
                />
                <span className={`font-bold text-sm w-12 flex-shrink-0 ${isDark ? 'text-white' : 'text-[#0F172A]'}`}>
                  {block.hour}
                </span>
                <span className={`text-sm flex-1 truncate ${isDark ? 'text-zinc-400' : 'text-slate-600'}`}>
                  {block.activity}
                </span>
                <span
                  className="text-xs rounded-full px-2 py-0.5 flex-shrink-0"
                  style={{
                    background: isDark ? '#27272a' : '#f1f5f9',
                    color: isDark ? '#a1a1aa' : '#475569',
                  }}
                >
                  {block.app}
                </span>
                {isPeak && (
                  <span className="bg-indigo-100 text-indigo-700 text-xs rounded-full px-2 py-0.5 flex-shrink-0">
                    peak
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// CoworkHandoff (automation score, top apps, downloads, SKILL.md preview)
// ---------------------------------------------------------------------------
const CoworkHandoff = ({ profile, isDark }) => {
  const score = profile.automation_readiness;
  const percentage = Math.round(score * 100);
  const color = automationColor(score);
  const label = automationLabel(score);
  const bg = automationBg(score, isDark);
  const maxAppTime = Math.max(...(profile.top_apps || []).map((a) => a.time_spent), 1);
  const safeName = (profile.person?.name || 'user').replace(/\s+/g, '-').toLowerCase();
  const automatableClusters = (profile.skill_clusters || []).filter((c) => c.automatable);

  return (
    <div className="flex flex-col gap-6">
      {/* Big automation score */}
      <div
        className="rounded-xl p-8 flex flex-col items-center gap-2 text-center"
        style={{ background: bg }}
      >
        <span className="text-6xl font-bold" style={{ color }}>{percentage}%</span>
        <span className="text-lg font-semibold" style={{ color }}>{label}</span>
        <p className={`text-sm max-w-md mt-1 ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
          Based on {automatableClusters.length} automatable skill
          clusters out of {(profile.skill_clusters || []).length} identified
        </p>
      </div>

      {/* Top apps by time */}
      <div
        className="rounded-xl border p-5"
        style={{
          background: isDark ? '#18181b' : '#ffffff',
          borderColor: isDark ? '#27272a' : '#e2e8f0',
        }}
      >
        <h3 className={`font-semibold mb-4 ${isDark ? 'text-white' : 'text-[#0F172A]'}`}>
          Top Applications by Time
        </h3>
        <div className="flex flex-col gap-3">
          {(profile.top_apps || []).slice(0, 8).map((app) => {
            const pctVal = Math.round((app.time_spent / maxAppTime) * 100);
            const minutes = Math.round(app.time_spent / 60);
            return (
              <div key={app.name} className="flex flex-col gap-1">
                <div className="flex items-center justify-between text-sm">
                  <span className={`font-medium ${isDark ? 'text-white' : 'text-[#0F172A]'}`}>{app.name}</span>
                  <span className={`text-xs ${isDark ? 'text-zinc-500' : 'text-slate-500'}`}>{minutes}m</span>
                </div>
                <div className={`h-2 rounded-full overflow-hidden ${isDark ? 'bg-zinc-800' : 'bg-slate-100'}`}>
                  <div
                    className="h-full bg-indigo-400 rounded-full transition-all duration-700"
                    style={{ width: `${pctVal}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Cowork automation actions */}
      {automatableClusters.length > 0 && (
        <div
          className="rounded-xl border p-5"
          style={{
            background: isDark ? '#18181b' : '#ffffff',
            borderColor: isDark ? '#27272a' : '#e2e8f0',
          }}
        >
          <h3 className={`font-semibold mb-3 ${isDark ? 'text-white' : 'text-[#0F172A]'}`}>
            Cowork Automation Actions
          </h3>
          <div className="flex flex-col gap-2">
            {automatableClusters.map((c) => (
              <div key={c.cowork_action} className="flex items-center gap-3">
                <span
                  className="text-xs bg-indigo-50 text-indigo-700 rounded px-2 py-1 flex-shrink-0"
                  style={{ fontFamily: 'monospace' }}
                >
                  {c.cowork_action}
                </span>
                <span className={`text-sm ${isDark ? 'text-zinc-400' : 'text-slate-600'}`}>{c.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Download buttons */}
      <div className="flex gap-3">
        <button
          onClick={() => downloadFile(profile.skill_md || '', `${safeName}-skill-profile.md`)}
          className="flex-1 rounded-lg py-3 text-sm font-medium transition-colors duration-150 flex items-center justify-center gap-2"
          style={{ background: '#0F172A', color: '#ffffff' }}
        >
          <Download className="h-4 w-4" />
          Download SKILL.md
        </button>
        <button
          onClick={() =>
            downloadFile(
              JSON.stringify(profile, null, 2),
              `${safeName}-skill-profile.json`,
              'application/json'
            )
          }
          className="flex-1 rounded-lg py-3 text-sm font-medium transition-colors duration-150 flex items-center justify-center gap-2"
          style={{ background: '#6366F1', color: '#ffffff' }}
        >
          <Download className="h-4 w-4" />
          Download JSON
        </button>
      </div>

      {/* SKILL.md preview */}
      {profile.skill_md && (
        <div
          className="rounded-xl border overflow-hidden"
          style={{ borderColor: isDark ? '#27272a' : '#e2e8f0' }}
        >
          <div
            className="px-5 py-3 border-b flex items-center justify-between"
            style={{ borderColor: isDark ? '#27272a' : '#e2e8f0' }}
          >
            <h3 className={`font-semibold text-sm ${isDark ? 'text-white' : 'text-[#0F172A]'}`}>
              SKILL.md Preview
            </h3>
            <span className="text-xs text-slate-400" style={{ fontFamily: 'monospace' }}>
              terminal
            </span>
          </div>
          <pre
            className="p-5 text-xs overflow-auto max-h-96 leading-relaxed"
            style={{
              fontFamily: 'monospace',
              background: isDark ? '#09090b' : '#020617',
              color: '#4ade80',
            }}
          >
            {profile.skill_md}
          </pre>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// TeamMemberCard
// ---------------------------------------------------------------------------
const TeamMemberCard = ({ member, isDark, onSelect }) => {
  const color = getAvatarColor(member.name);
  const avatarSrc = resolveUserAvatar(member);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ scale: 1.01 }}
      onClick={() => onSelect(member)}
      className="cursor-pointer rounded-xl border p-4 flex items-center gap-4 transition-all duration-200"
      style={{
        background: isDark ? '#18181b' : '#ffffff',
        borderColor: isDark ? '#27272a' : '#e2e8f0',
      }}
    >
      {avatarSrc ? (
        <img src={avatarSrc} alt="" className="w-11 h-11 rounded-full flex-shrink-0 object-cover" />
      ) : (
      <div
        className="w-11 h-11 rounded-full flex items-center justify-center text-sm font-bold !text-white flex-shrink-0"
        style={{ background: color }}
      >
        {getInitials(member.name)}
      </div>
      )}

      <div className="flex-1 min-w-0">
        <p className={`font-semibold text-sm truncate ${isDark ? 'text-white' : 'text-zinc-900'}`}>
          {member.name || member.email}
        </p>
        <p className={`text-xs truncate ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
          {member.role || 'Team Member'}
        </p>
      </div>

      <div className="flex items-center gap-2 flex-shrink-0">
        {member.has_cached_profile ? (
          <span
            className="text-xs px-2.5 py-1 rounded-full font-medium"
            style={{
              background: isDark ? '#27272a' : '#eef2ff',
              color: '#6366f1',
            }}
          >
            {(member.skill_clusters_count || 0)} clusters
          </span>
        ) : (
          <span
            className="text-xs px-2.5 py-1 rounded-full"
            style={{
              background: isDark ? '#27272a' : '#f4f4f5',
              color: isDark ? '#a1a1aa' : '#71717a',
            }}
          >
            Analyse Skills →
          </span>
        )}
        <ChevronRight className={`h-4 w-4 ${isDark ? 'text-zinc-600' : 'text-zinc-400'}`} />
      </div>
    </motion.div>
  );
};

// ---------------------------------------------------------------------------
// ProfileDetailView (person card + 3 tabs)
// ---------------------------------------------------------------------------
const ProfileDetailView = ({ member, profile, isDark, onBack, onRegenerate, generating }) => {
  const [activeTab, setActiveTab] = useState('clusters');
  const displayName = profile?.person?.name || member?.name;
  const avatarSrc = resolveUserAvatar(profile?.person || member);

  const tabs = [
    { id: 'clusters', label: 'Skill Clusters' },
    { id: 'timeline', label: 'Activity Timeline' },
    { id: 'handoff', label: 'Cowork Handoff' },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
    >
      {/* Back button */}
      <button
        onClick={onBack}
        className="flex items-center gap-1.5 text-sm mb-6 transition-colors duration-150"
        style={{ color: isDark ? '#a1a1aa' : '#64748b' }}
      >
        <ArrowLeft size={16} />
        Back to People
      </button>

      {/* Person info card */}
      <div
        className="rounded-xl border shadow-sm p-6 mb-6"
        style={{
          background: isDark ? '#18181b' : '#ffffff',
          borderColor: isDark ? '#27272a' : '#e2e8f0',
        }}
      >
        <div className="flex items-start gap-5">
          {avatarSrc ? (
            <img src={avatarSrc} alt="" className="w-14 h-14 rounded-full flex-shrink-0 object-cover" />
          ) : (
          <div
            className="w-14 h-14 rounded-full flex items-center justify-center !text-white font-bold text-lg flex-shrink-0"
            style={{ background: getAvatarColor(displayName) }}
          >
            {getInitials(displayName)}
          </div>
          )}

          <div className="flex-1 min-w-0">
            <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-[#0F172A]'}`}>
              {displayName}
            </h2>
            <p className={`text-sm mt-0.5 ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
              {[profile?.person?.role, profile?.person?.department].filter(Boolean).join(' · ') || 'Team Member'}
            </p>
            <div className={`flex flex-wrap gap-4 mt-3 text-xs ${isDark ? 'text-zinc-500' : 'text-slate-500'}`}>
              <span>{(profile.total_screenshots || 0).toLocaleString()} screenshots</span>
              <span>·</span>
              <span>{(profile.total_ocr_chunks || 0).toLocaleString()} OCR chunks</span>
              <span>·</span>
              <span>Generated {new Date(profile.generated_at).toLocaleString()}</span>
            </div>
          </div>

          <div className="flex flex-col items-end gap-3 flex-shrink-0">
            <AutomationRing score={profile.automation_readiness || 0} size={120} />
            <button
              onClick={() => onRegenerate(member._id)}
              disabled={generating}
              className="px-5 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150 disabled:opacity-50"
              style={{
                background: generating ? (isDark ? '#27272a' : '#e2e8f0') : '#6366F1',
                color: generating ? (isDark ? '#71717a' : '#94a3b8') : '#ffffff',
              }}
            >
              {generating ? 'Analysing...' : 'Re-analyse'}
            </button>
          </div>
        </div>
      </div>

      {/* 3 sub-tabs */}
      <div className="flex gap-2 mb-6">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className="px-4 py-2 rounded-full text-sm font-medium transition-all duration-150"
            style={{
              background: activeTab === tab.id ? '#6366F1' : (isDark ? '#18181b' : '#ffffff'),
              color: activeTab === tab.id ? '#ffffff' : (isDark ? '#a1a1aa' : '#475569'),
              border: activeTab === tab.id ? 'none' : `1px solid ${isDark ? '#27272a' : '#e2e8f0'}`,
              boxShadow: activeTab === tab.id ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'clusters' && (
        <div className="flex flex-col gap-5">
          {/* Summary box */}
          <div
            className="rounded-xl p-5"
            style={{ background: isDark ? '#1e1b4b20' : '#eef2ff' }}
          >
            <p className={`text-sm italic leading-relaxed ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>
              {profile.summary}
            </p>
          </div>
          {/* Cluster cards grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(profile.skill_clusters || []).map((cluster, i) => (
              <SkillClusterCard key={i} cluster={cluster} isDark={isDark} />
            ))}
          </div>
        </div>
      )}

      {activeTab === 'timeline' && (
        <ActivityTimeline
          timeline={profile.timeline || []}
          peakHours={profile.peak_hours || []}
          isDark={isDark}
        />
      )}

      {activeTab === 'handoff' && (
        <CoworkHandoff profile={profile} isDark={isDark} />
      )}
    </motion.div>
  );
};

// ---------------------------------------------------------------------------
// Main KnowSkills component
// ---------------------------------------------------------------------------
const KnowSkills = () => {
  const { mode } = useTheme();
  const isDark = mode === 'dark';
  useAuth();

  const [teamMembers, setTeamMembers] = useState([]);
  const [loadingTeam, setLoadingTeam] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1, page: 1, limit: 12 });
  const MEMBERS_PER_PAGE = 12;
  const searchDebounceRef = useRef(null);

  const [selectedMember, setSelectedMember] = useState(null);
  const [activeProfile, setActiveProfile] = useState(null);
  const [generatingFor, setGeneratingFor] = useState(null);
  const [loadingStep, setLoadingStep] = useState('');

  const stepIntervalRef = useRef(null);

  const startStepAnimation = useCallback(() => {
    let i = 0;
    setLoadingStep(LOADING_STEPS[0]);
    stepIntervalRef.current = setInterval(() => {
      i = Math.min(i + 1, LOADING_STEPS.length - 1);
      setLoadingStep(LOADING_STEPS[i]);
    }, 1500);
  }, []);

  const stopStepAnimation = useCallback(() => {
    if (stepIntervalRef.current) {
      clearInterval(stepIntervalRef.current);
      stepIntervalRef.current = null;
    }
    setLoadingStep('');
  }, []);

  useEffect(() => {
    return () => {
      if (stepIntervalRef.current) clearInterval(stepIntervalRef.current);
    };
  }, []);

  // Fetch team (server-side pagination + search)
  const fetchTeam = useCallback(async (page = 1, search = '') => {
    try {
      setLoadingTeam(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ page, limit: MEMBERS_PER_PAGE });
      if (search) params.set('search', search);
      const res = await fetch(`${SERVER_URL}/api/v1/skill-profile/team/all?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (json.success) {
        setTeamMembers(json.data);
        if (json.pagination) setPagination(json.pagination);
      } else {
        toast.error('Failed to load team members');
      }
    } catch (err) {
      toast.error('Failed to load team members');
    } finally {
      setLoadingTeam(false);
    }
  }, []);

  useEffect(() => {
    fetchTeam(1, '');
  }, [fetchTeam]);

  // Select member → generate/fetch profile
  const handleSelectMember = useCallback(async (member) => {
    setSelectedMember(member);
    setActiveProfile(null);
    setGeneratingFor(member._id);
    startStepAnimation();
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${SERVER_URL}/api/v1/skill-profile/${member._id}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ forceRegenerate: false }),
      });
      const json = await res.json();
      if (json.success) {
        setActiveProfile(json.data);
        setTeamMembers((prev) =>
          prev.map((m) =>
            m._id === member._id
              ? {
                  ...m,
                  has_cached_profile: true,
                  automation_readiness: json.data.automation_readiness,
                  skill_clusters_count: (json.data.skill_clusters || []).length,
                  cached_at: json.data.generated_at,
                }
              : m
          )
        );
      } else {
        toast.error(json.message || 'Failed to generate profile');
        setSelectedMember(null);
      }
    } catch (err) {
      toast.error('Failed to generate profile');
      setSelectedMember(null);
    } finally {
      stopStepAnimation();
      setGeneratingFor(null);
    }
  }, [startStepAnimation, stopStepAnimation]);

  // Force regenerate
  const handleRegenerate = useCallback(async (userId) => {
    setGeneratingFor(userId);
    setActiveProfile(null);
    startStepAnimation();
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${SERVER_URL}/api/v1/skill-profile/${userId}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ forceRegenerate: true }),
      });
      const json = await res.json();
      if (json.success) {
        setActiveProfile(json.data);
        toast.success('Profile regenerated');
      } else {
        toast.error(json.message || 'Failed to regenerate');
      }
    } catch (err) {
      toast.error('Failed to regenerate');
    } finally {
      stopStepAnimation();
      setGeneratingFor(null);
    }
  }, [startStepAnimation, stopStepAnimation]);

  const handleBack = useCallback(() => {
    setSelectedMember(null);
    setActiveProfile(null);
  }, []);

  // Page change handler
  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
    fetchTeam(page, searchQuery);
  }, [fetchTeam, searchQuery]);

  // Debounced search — resets to page 1
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setCurrentPage(1);
      fetchTeam(1, searchQuery);
    }, 400);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [searchQuery, fetchTeam]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="w-full">
      <AnimatePresence mode="wait">
        {selectedMember ? (
          <div key="detail">
            {/* Loading state */}
            {generatingFor === selectedMember._id && !activeProfile ? (
              <motion.div
                key="generating"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                {/* Back button during loading */}
                <button
                  onClick={handleBack}
                  className="flex items-center gap-1.5 text-sm mb-6 transition-colors duration-150"
                  style={{ color: isDark ? '#a1a1aa' : '#64748b' }}
                >
                  <ArrowLeft size={16} />
                  Back to People
                </button>

                {/* Loading banner */}
                <div
                  className="rounded-xl p-5 mb-6 flex items-center gap-4"
                  style={{
                    background: isDark ? '#1e1b4b20' : '#eef2ff',
                    border: `1px solid ${isDark ? '#312e81' : '#c7d2fe'}`,
                  }}
                >
                  <div
                    className="w-5 h-5 border-2 border-indigo-500 rounded-full animate-spin flex-shrink-0"
                    style={{ borderTopColor: 'transparent' }}
                  />
                  <div>
                    <p className={`font-medium text-sm ${isDark ? 'text-indigo-300' : 'text-indigo-800'}`}>
                      {loadingStep}
                    </p>
                    <p className={`text-xs mt-0.5 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`}>
                      This may take 15–30 seconds while TeamGrid analyses the activity data
                    </p>
                  </div>
                </div>
              </motion.div>
            ) : activeProfile ? (
              <ProfileDetailView
                key="profile"
                member={selectedMember}
                profile={activeProfile}
                isDark={isDark}
                onBack={handleBack}
                onRegenerate={handleRegenerate}
                generating={generatingFor === selectedMember._id}
              />
            ) : null}
          </div>
        ) : (
          /* Team list view */
          <motion.div
            key="list"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-[#0F172A]'}`}>
                  People
                </h3>
                <p className={`text-xs mt-0.5 ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                  {pagination.total} team member{pagination.total !== 1 ? 's' : ''}
                </p>
              </div>
              <button
                onClick={() => fetchTeam(currentPage, searchQuery)}
                disabled={loadingTeam}
                className="p-2 rounded-lg border transition-all"
                style={{
                  background: isDark ? '#27272a' : '#f4f4f5',
                  borderColor: isDark ? '#3f3f46' : '#d4d4d8',
                }}
                title="Refresh"
              >
                <RefreshCw
                  className={`h-4 w-4 ${loadingTeam ? 'animate-spin' : ''} ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}
                />
              </button>
            </div>

            {/* Search bar */}
            <div className="relative mb-4">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4"
                style={{ color: isDark ? '#52525b' : '#94a3b8' }}
              />
              <input
                type="text"
                placeholder="Search by name, role, or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 rounded-lg border text-sm outline-none transition-all"
                style={{
                  background: isDark ? '#09090b' : '#f8fafc',
                  borderColor: isDark ? '#27272a' : '#e2e8f0',
                  color: isDark ? '#ffffff' : '#0f172a',
                }}
              />
            </div>

            {/* Loading skeleton */}
            {loadingTeam ? (
              <div className="flex flex-col gap-3">
                {[1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className="h-16 rounded-xl animate-pulse"
                    style={{ background: isDark ? '#27272a' : '#f4f4f5' }}
                  />
                ))}
              </div>
            ) : teamMembers.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <Users className={`h-10 w-10 ${isDark ? 'text-zinc-700' : 'text-zinc-300'}`} />
                <p className={`text-sm ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>
                  {searchQuery ? 'No matching members found' : 'No team members found'}
                </p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {teamMembers.map((member) => (
                    <TeamMemberCard
                      key={member._id}
                      member={member}
                      isDark={isDark}
                      onSelect={handleSelectMember}
                    />
                  ))}
                </div>

                {/* Pagination */}
                {pagination.totalPages > 1 && (
                  <div className="flex items-center justify-between mt-6 pt-4 border-t" style={{ borderColor: isDark ? '#27272a' : '#e4e4e7' }}>
                    <p className={`text-xs ${isDark ? 'text-zinc-500' : 'text-slate-500'}`}>
                      Showing {(currentPage - 1) * MEMBERS_PER_PAGE + 1}–{Math.min(currentPage * MEMBERS_PER_PAGE, pagination.total)} of {pagination.total}
                    </p>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="p-2 rounded-lg text-sm transition-all disabled:opacity-30"
                        style={{
                          background: isDark ? '#27272a' : '#f4f4f5',
                          color: isDark ? '#d4d4d8' : '#3f3f46',
                        }}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </button>
                      {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                        .filter((page) => {
                          if (pagination.totalPages <= 7) return true;
                          if (page === 1 || page === pagination.totalPages) return true;
                          if (Math.abs(page - currentPage) <= 1) return true;
                          return false;
                        })
                        .reduce((acc, page, idx, arr) => {
                          if (idx > 0 && page - arr[idx - 1] > 1) {
                            acc.push('...');
                          }
                          acc.push(page);
                          return acc;
                        }, [])
                        .map((page, idx) =>
                          page === '...' ? (
                            <span key={`dots-${idx}`} className={`px-2 text-xs ${isDark ? 'text-zinc-600' : 'text-zinc-400'}`}>...</span>
                          ) : (
                            <button
                              key={page}
                              onClick={() => handlePageChange(page)}
                              className="min-w-[32px] h-8 rounded-lg text-xs font-medium transition-all"
                              style={{
                                background: currentPage === page
                                  ? '#6366f1'
                                  : (isDark ? '#27272a' : '#f4f4f5'),
                                color: currentPage === page
                                  ? '#ffffff'
                                  : (isDark ? '#d4d4d8' : '#3f3f46'),
                              }}
                            >
                              {page}
                            </button>
                          )
                        )}
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === pagination.totalPages}
                        className="p-2 rounded-lg text-sm transition-all disabled:opacity-30"
                        style={{
                          background: isDark ? '#27272a' : '#f4f4f5',
                          color: isDark ? '#d4d4d8' : '#3f3f46',
                        }}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default KnowSkills;
